import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Constants } from 'app/core/constant/app-constant';
import { OTP_DATA } from 'app/core/enums/sign-in.enum';
import { TranslatePipe } from 'app/core/pipe/translate.pipe';
import { DecryptionService } from 'app/core/services/decryption.service';
import { ToasterService } from 'app/core/services/toaster.service';
import { UserCountryService } from 'app/core/services/user-country.service';
import { EmailOtpFieldComponent } from 'app/shared/components/molecules/email-otp-field';
import { PhoneOtpFieldComponent } from 'app/shared/components/molecules/phone-otp-field';
import { FilmmakerSignupService } from 'app/shared/services/filmmaker-signup.service';
import { Subject, takeUntil } from 'rxjs';

export interface ChangePasswordDialogData {
    email?: string;
    phoneNumber?: string;
    countryCode?: string;
    countryCodeNumeric?: string;
    isPasswordSet?: boolean;
}

@Component({
    selector: 'app-change-password-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslatePipe,
        EmailOtpFieldComponent,
        PhoneOtpFieldComponent,
    ],
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['./change-password-dialog.component.scss'],
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
    isIndian: boolean = false;
    isOtpVerified = signal(false);
    isProcessing = signal(false);
    isOtpSent = signal(false);
    emailControl = new FormControl('', [Validators.required, Validators.email]);
    phoneControl = new FormControl('', [Validators.required]);
    countryCodeControl = new FormControl(Constants.IndianDetails.dialCode, [
        Validators.required,
    ]);
    isoCountryCodeControl = new FormControl(
        Constants.IndianDetails.countryCode,
        [Validators.required]
    );
    emailOtpControl = new FormControl('', [Validators.required]);
    phoneOtpControl = new FormControl('', [Validators.required]);
    emailVerified = signal(false);
    phoneVerified = signal(false);
    isWaNumber = signal(false);
    isCreatePassword = signal(false);
    otpType = OTP_DATA.VERIFY_PHONE;

    private readonly _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ChangePasswordDialogData,
        private readonly _signupService: FilmmakerSignupService,
        private readonly _toasterService: ToasterService,
        private readonly _userCountryService: UserCountryService,
        private readonly _router: Router,
        private readonly _decryptionService: DecryptionService
    ) {}

    ngOnInit(): void {
        // Determine if user is Indian
        this.isIndian =
            this._userCountryService._countryCode().toLowerCase() ===
            Constants.IndianDetails.countryCode.toLowerCase();

        // Pre-fill form with user data and disable fields
        if (this.data.email) {
            this.emailControl.setValue(this.data.email);
            this.emailControl.disable(); // Disable email field
            this.emailControl.markAsTouched();
            this.emailControl.updateValueAndValidity();
        }

        if (this.data.phoneNumber) {
            this.phoneControl.setValue(this.data.phoneNumber);
            this.phoneControl.disable(); // Disable phone field
            this.phoneControl.markAsTouched();
            this.phoneControl.updateValueAndValidity();
        }

        if (this.data.countryCode) {
            this.isoCountryCodeControl.setValue(this.data.countryCode);
            this.isoCountryCodeControl.disable(); // Disable country code field
            this.isoCountryCodeControl.markAsTouched();
            this.isoCountryCodeControl.updateValueAndValidity();
        }

        if (this.data.countryCodeNumeric) {
            this.countryCodeControl.setValue(this.data.countryCodeNumeric);
            this.countryCodeControl.disable(); // Disable country code numeric field
            this.countryCodeControl.markAsTouched();
            this.countryCodeControl.updateValueAndValidity();
        }

        this.isCreatePassword.set(!this.data?.isPasswordSet);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Handle email OTP sent
     */
    onEmailOtpSent(email: string): void {
        this.isOtpSent.set(true);
        this.isProcessing.set(false);
        this._toasterService.displaySnackBar(
            'OTP sent successfully to your email!',
            'success'
        );
    }

    /**
     * Handle email OTP verification
     */
    onEmailVerificationStatusChange(data: {
        email: string;
        isVerified: boolean;
    }): void {
        this.emailVerified.set(data.isVerified);
        this.checkVerificationComplete();
    }

    /**
     * Handle phone OTP sent
     */
    onPhoneOtpSent(data: { phone: string; countryCode: string }): void {
        this.isOtpSent.set(true);
        this.isProcessing.set(false);
        this._toasterService.displaySnackBar(
            'OTP sent successfully to your phone!',
            'success'
        );
    }

    /**
     * Handle phone OTP verification
     */
    onPhoneVerificationStatusChange(data: {
        phone: string;
        countryCode: string;
        isVerified: boolean;
    }): void {
        this.phoneVerified.set(data.isVerified);
        this.checkVerificationComplete();
    }

    /**
     * Handle WhatsApp number checkbox change
     */
    onWaNumberChange(isWaNumber: boolean): void {
        this.isWaNumber.set(isWaNumber);
    }

    /**
     * Send OTP manually (triggered by Continue button)
     */
    sendOtp(): void {
        if (this.isIndian) {
            // Send phone OTP
            // Get values from disabled controls
            const phone = this.phoneControl.value;
            const dialingCode = this.countryCodeControl.value;
            const isoCountryCode = this.isoCountryCodeControl.value;

            if (
                !phone ||
                !dialingCode ||
                !isoCountryCode ||
                this.isProcessing()
            ) {
                return;
            }

            this.isProcessing.set(true);

            this._signupService
                .sendPhoneOtp(
                    phone,
                    isoCountryCode,
                    dialingCode,
                    this.isWaNumber(),
                    OTP_DATA.VERIFY_PHONE
                )
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (response) => {
                        this.isOtpSent.set(true);
                        this.isProcessing.set(false);
                        this._toasterService.displaySnackBar(
                            'OTP sent successfully to your phone!',
                            'success'
                        );
                    },
                    error: (error) => {
                        this.isProcessing.set(false);
                        this._toasterService.displaySnackBar(
                            error.error?.message ||
                                error.message ||
                                'Failed to send OTP',
                            'error'
                        );
                        console.error('Send phone OTP error:', error);
                    },
                });
        } else {
            // Send email OTP
            // Get value from disabled control
            const email = this.emailControl.value;

            if (!email || this.isProcessing()) {
                return;
            }
            this.isProcessing.set(true);

            this._signupService
                .sendEmailOtp(email)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (response) => {
                        this.isOtpSent.set(true);
                        this.isProcessing.set(false);
                        this._toasterService.displaySnackBar(
                            'OTP sent successfully to your email!',
                            'success'
                        );
                    },
                    error: (error) => {
                        this.isProcessing.set(false);
                        this._toasterService.displaySnackBar(
                            error.error?.message ||
                                error.message ||
                                'Failed to send OTP',
                            'error'
                        );
                        console.error('Send email OTP error:', error);
                    },
                });
        }
    }

    /**
     * Handle OTP complete (when user enters 6 digits)
     */
    onOtpComplete(otp: string): void {
        if (this.isIndian) {
            this.phoneOtpControl.setValue(otp);
            this.verifyPhoneOtp(otp);
        } else {
            this.emailOtpControl.setValue(otp);
            this.verifyEmailOtp(otp);
        }
    }

    /**
     * Verify email OTP
     */
    private verifyEmailOtp(otp: string): void {
        // Get value from disabled control
        const email = this.emailControl.value;

        if (!email || this.isProcessing()) {
            return;
        }
        this.isProcessing.set(true);

        this._signupService
            .verifyEmailOtp(email, otp)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.isProcessing.set(false);
                    this.emailVerified.set(true);
                    this.checkVerificationComplete();
                    this._toasterService.displaySnackBar(
                        'Email verified successfully',
                        'success'
                    );
                },
                error: (error) => {
                    this.isProcessing.set(false);
                    this.emailOtpControl.setValue('');
                    this._toasterService.displaySnackBar(
                        error.error?.message ||
                            error.message ||
                            'Failed to verify OTP',
                        'error'
                    );
                    console.error('Verify email OTP error:', error);
                },
            });
    }

    /**
     * Verify phone OTP
     */
    private verifyPhoneOtp(otp: string): void {
        // Get values from disabled controls
        const phone = this.phoneControl.value;
        const dialingCode = this.countryCodeControl.value;
        const isoCountryCode = this.isoCountryCodeControl.value;

        if (!phone || !dialingCode || !isoCountryCode || this.isProcessing()) {
            return;
        }

        this.isProcessing.set(true);

        this._signupService
            .verifyPhoneOtp(phone, otp, isoCountryCode, dialingCode)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.isProcessing.set(false);
                    this.phoneVerified.set(true);
                    this.checkVerificationComplete();
                    this._toasterService.displaySnackBar(
                        'Phone verified successfully',
                        'success'
                    );
                },
                error: (error) => {
                    this.isProcessing.set(false);
                    this.phoneOtpControl.setValue('');
                    this._toasterService.displaySnackBar(
                        error.error?.message ||
                            error.message ||
                            'Failed to verify OTP',
                        'error'
                    );
                    console.error('Verify phone OTP error:', error);
                },
            });
    }

    /**
     * Check if verification is complete
     */
    private checkVerificationComplete(): void {
        const isVerified = this.isIndian
            ? this.phoneVerified()
            : this.emailVerified();
        this.isOtpVerified.set(isVerified);
    }

    /**
     * Handle send OTP button click
     */
    onSendOtp(): void {
        this.sendOtp();
    }

    /**
     * Handle continue button click (only after OTP is verified)
     */
    onContinue(): void {
        // OTP must be verified before continuing
        if (!this.isOtpVerified()) {
            return;
        }

        const identifier = this.isIndian
            ? this.phoneControl.value
            : this.emailControl.value;

        // Generate a verification token with timestamp
        const verificationData = {
            identifier: identifier,
            timestamp: Date.now(),
            verified: true,
        };

        // Encrypt the verification data
        this._decryptionService
            .encryptData(JSON.stringify(verificationData), 'CHANGE_PASSWORD')
            .subscribe({
                next: (encryptedToken) => {
                    // Store encrypted token in sessionStorage (valid for 10 minutes)
                    sessionStorage.setItem('pwd_change_token', encryptedToken);

                    // Close dialog
                    this.dialogRef.close({
                        verified: true,
                        identifier: identifier,
                    });
                },
                error: (error) => {
                    console.error('Encryption error:', error);
                    this._toasterService.displaySnackBar(
                        'Failed to process verification. Please try again.',
                        'error'
                    );
                },
            });
    }

    /**
     * Handle cancel/close
     */
    onCancel(): void {
        this.dialogRef.close({ verified: false });
    }
}
